/**
 * Transaction types matching rewards-service API schemas.
 * These types mirror the Pydantic models in app/schemas/v1/transactions.py
 */

// Enums matching API
export type TransactionType = 'earn' | 'redeem' | 'adjustment';
export type SourceSystem = 'explorations' | 'admin' | 'rewards';
export type RewardType = 'exploration' | 'signup_bonus' | 'referral' | 'easter_egg' | 'customer_support';

// Context types - discriminated union based on reward_type or transaction type

export interface EarnExplorationContext {
  reward_type: 'exploration';
  ref_experience_id: string;
  experience_name: string;
  points: number;
  exploration_signup_id: number;
  transaction_notes?: string;
  internal_notes?: string;
}

export interface EarnSignupBonusContext {
  reward_type: 'signup_bonus';
  transaction_notes?: string;
  internal_notes?: string;
}

export interface EarnReferralContext {
  reward_type: 'referral';
  transaction_notes?: string;
  internal_notes?: string;
}

export interface EarnEasterEggContext {
  reward_type: 'easter_egg';
  transaction_notes?: string;
  internal_notes?: string;
}

export interface EarnCustomerSupportContext {
  reward_type: 'customer_support';
  transaction_notes?: string;
  internal_notes?: string;
}

export interface AdjustmentExplorationContext {
  reward_type: 'exploration';
  ref_experience_id: string;
  experience_name: string;
  exploration_signup_id: number;
  transaction_notes?: string;
  original_transaction_id?: string;
  internal_notes?: string;
}

export interface AdjustmentContext {
  transaction_notes: string;
  original_transaction_id: string;
  internal_notes?: string;
}

// Union types for context
// IMPORTANT: Order matches backend - more specific types first for proper type discrimination
export type EarnContext =
  | EarnExplorationContext
  | EarnSignupBonusContext
  | EarnReferralContext
  | EarnEasterEggContext
  | EarnCustomerSupportContext;

export type TransactionContextJson =
  | AdjustmentExplorationContext // Most specific - must come first
  | AdjustmentContext // More specific than EarnContext
  | EarnContext; // Least specific - comes last

// Transaction response from GET /internal/rewards/transactions
export interface TransactionResponse {
  transaction_id: string; // UUID
  community_ref_user_id: number;
  eligible_participant_id: number | string | null;
  partner_product_id: number | null;
  transaction_type: TransactionType;
  source_system: SourceSystem;
  point_value: number;
  transaction_datetime_utc: string; // ISO datetime
  created_at: string; // ISO datetime
  context_json: TransactionContextJson;
  reward_id: string | null; // UUID, only for REDEEM
  // Display fields (for REDEEM transactions)
  transaction_title?: string;
  transaction_subtitle?: string;
  dollar_value?: number;
}

// Paginated list response
export interface TransactionListResponse {
  transactions: TransactionResponse[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Detail response with related data
export interface TransactionDetailResponse {
  transaction: TransactionResponse;
  related_adjustments: TransactionResponse[];
  user_balance: number;
}

// Query parameters for GET /internal/rewards/transactions
export interface TransactionQueryParams {
  community_ref_user_id?: number;
  eligible_participant_id?: number | string;
  transaction_id?: string; // UUID
  exploration_signup_id?: number;
  partner_product_id?: number;
  transaction_type?: TransactionType;
  source_system?: SourceSystem;
  from_date?: string; // YYYY-MM-DD
  to_date?: string; // YYYY-MM-DD
  time_offset?: number; // Timezone offset in hours
  limit?: number;
  offset?: number;
}

// Create transaction request for POST /internal/rewards/transactions
export interface CreateTransactionRequest {
  source_system: SourceSystem;
  community_ref_user_id: number;
  eligible_participant_id?: number | string;
  partner_product_id?: number;
  transaction_type: 'earn' | 'adjustment'; // REDEEM not allowed on this endpoint
  point_value: number;
  transaction_datetime_utc: string; // ISO datetime
  context_json: TransactionContextJson;
}

// Create transaction response
export interface TransactionCreateResponse {
  transaction_id: string; // UUID
  created_at: string; // ISO datetime
  source_system: SourceSystem;
  community_ref_user_id: number;
  eligible_participant_id: number | string | null;
  partner_product_id: number | null;
  transaction_type: TransactionType;
  point_value: number;
  transaction_datetime_utc: string;
  context_json: TransactionContextJson;
}

// Type guards for context discrimination
export function isExplorationContext(
  context: TransactionContextJson
): context is EarnExplorationContext | AdjustmentExplorationContext {
  return 'reward_type' in context && context.reward_type === 'exploration';
}

export function isAdjustmentContext(
  context: TransactionContextJson
): context is AdjustmentContext {
  return 'original_transaction_id' in context && !('reward_type' in context);
}

export function hasRewardType(
  context: TransactionContextJson
): context is EarnContext | AdjustmentExplorationContext {
  return 'reward_type' in context;
}

// Helper to get human-readable reward type label
export function getRewardTypeLabel(rewardType: RewardType): string {
  const labels: Record<RewardType, string> = {
    exploration: 'Exploration Activity',
    signup_bonus: 'Signup Bonus',
    referral: 'Referral Bonus',
    easter_egg: 'Easter Egg',
    customer_support: 'Customer Support',
  };
  return labels[rewardType] || rewardType;
}

// Helper to get transaction type badge color
export function getTransactionTypeColor(type: TransactionType): string {
  const colors: Record<TransactionType, string> = {
    earn: 'success',
    redeem: 'info',
    adjustment: 'warning',
  };
  return colors[type] || 'default';
}

// Timezone options for the filter dropdown
export interface TimezoneOption {
  label: string;
  value: number;
  abbreviation: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { label: 'Pacific Time (PST/PDT)', value: -8, abbreviation: 'PST' },
  { label: 'Mountain Time (MST/MDT)', value: -7, abbreviation: 'MST' },
  { label: 'Central Time (CST/CDT)', value: -6, abbreviation: 'CST' },
  { label: 'Eastern Time (EST/EDT)', value: -5, abbreviation: 'EST' },
  { label: 'UTC', value: 0, abbreviation: 'UTC' },
];

// Transaction type options for filter dropdowns
// Values match backend enum: TransactionType in app/schemas/v1/transactions.py
export function getTransactionTypeOptions(): { value: TransactionType | ''; label: string }[] {
  return [
    { value: '', label: 'All Types' },
    { value: 'earn', label: 'Earn' },
    { value: 'redeem', label: 'Redeem' },
    { value: 'adjustment', label: 'Adjustment' },
  ];
}

// Source system options for filter dropdowns
// Values match backend enum: SourceSystem in app/schemas/v1/transactions.py
export function getSourceSystemOptions(): { value: SourceSystem | ''; label: string }[] {
  return [
    { value: '', label: 'All Sources' },
    { value: 'explorations', label: 'Explorations' },
    { value: 'admin', label: 'Admin' },
    { value: 'rewards', label: 'Rewards' },
  ];
}

